
import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import ConfettiEffect from './ConfettiEffect';

interface PACTExample {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

// Componente de indicador hecho a mano mejorado
const HandDrawnClickIndicator = ({ className }: { className?: string }) => (
  <div className={`flex flex-col items-center ${className}`}>
    <div className="font-handwritten text-lg font-bold text-slate-dark mb-1 transform -rotate-2">
      Click here
    </div>
    <div className="text-2xl animate-bounce">👆</div>
  </div>
);

const InteractivePACTsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedPACTs, setCompletedPACTs] = useState<Set<string>>(new Set());
  const [clickedPACTs, setClickedPACTs] = useState<Set<string>>(new Set());
  const [showConfetti, setShowConfetti] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const pactExamples: PACTExample[] = [
    {
      id: 'read',
      title: 'Read 10 pages',
      description: 'Daily reading habit',
      icon: '📚',
      color: 'from-blue-primary to-blue-light'
    },
    {
      id: 'meditate',
      title: 'Meditate 5 min',
      description: 'Mindfulness practice',
      icon: '🧘‍♀️',
      color: 'from-purple-accent to-pink-400'
    },
    {
      id: 'exercise',
      title: 'Exercise 20 min',
      description: 'Stay active',
      icon: '💪',
      color: 'from-green-success to-emerald-400'
    },
    {
      id: 'plan',
      title: 'Plan the week',
      description: 'Organization habit',
      icon: '📅',
      color: 'from-orange-accent to-orange-light'
    },
    {
      id: 'water',
      title: 'Drink 8 glasses',
      description: 'Hydration goal',
      icon: '💧',
      color: 'from-cyan-400 to-blue-light'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        setCurrentIndex((prev) => (prev + 1) % pactExamples.length);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isAnimating, pactExamples.length]);

  const handlePACTClick = (pactId: string) => {
    if (completedPACTs.has(pactId)) return;

    setClickedPACTs(prev => new Set([...prev, pactId]));
    setIsAnimating(true);
    setCompletedPACTs(prev => new Set([...prev, pactId]));
    setShowConfetti(true);

    setTimeout(() => {
      setIsAnimating(false);
    }, 1000);
  };

  const resetCompleted = () => {
    setCompletedPACTs(new Set());
    setClickedPACTs(new Set());
  };

  const currentPACT = pactExamples[currentIndex];
  const isCurrentCompleted = completedPACTs.has(currentPACT.id);
  const isCurrentClicked = clickedPACTs.has(currentPACT.id);

  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Main PACT Display */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-whisper to-white shadow-lg border border-slate-soft/50 backdrop-blur-sm organic-border">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-slate-dark mb-2">Try a PACT</h3>
            <p className="text-sm text-slate-lighter">Click any PACT to experience completion</p>
          </div>

          {/* Current PACT */}
          <div 
            className={`relative cursor-pointer transition-all duration-300 transform hover:scale-105 ${
              isAnimating ? 'animate-bounce-in' : 'animate-scale-in'
            }`}
            onClick={() => handlePACTClick(currentPACT.id)}
          >
            <div className={`bg-gradient-to-r ${currentPACT.color} rounded-xl p-4 text-white shadow-lg hover:shadow-xl transition-all duration-300`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{currentPACT.icon}</span>
                  <div>
                    <h4 className="font-semibold text-lg">{currentPACT.title}</h4>
                    <p className="text-white/80 text-sm">{currentPACT.description}</p>
                  </div>
                </div>
                <div className="relative">
                  <div className={`w-8 h-8 rounded-full border-2 border-white/50 flex items-center justify-center transition-all duration-300 ${
                    isCurrentCompleted 
                      ? 'bg-white text-green-success scale-110' 
                      : 'hover:border-white hover:bg-white/10'
                  }`}>
                    {isCurrentCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : null}
                  </div>
                  
                  {/* Indicador visual mejorado */}
                  {!isCurrentClicked && !isCurrentCompleted && (
                    <div className="absolute -bottom-16 -right-8 z-10">
                      <HandDrawnClickIndicator className="animate-bounce" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {pactExamples.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-blue-primary w-6' 
                    : 'bg-slate-soft hover:bg-slate-light'
                }`}
              />
            ))}
          </div>

          {/* Completed Counter */}
          {completedPACTs.size > 0 && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 bg-green-soft text-green-success px-4 py-2 rounded-full text-sm font-medium organic-border">
                <span className="text-lg">🎉</span>
                {completedPACTs.size} PACT{completedPACTs.size > 1 ? 's' : ''} completed!
              </div>
              <button
                onClick={resetCompleted}
                className="ml-2 text-xs text-slate-lighter hover:text-slate-light transition-colors duration-200"
              >
                Reset
              </button>
            </div>
          )}
        </div>

        {/* Animated background elements */}
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-primary/10 to-transparent rounded-full transform translate-x-8 -translate-y-8"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-orange-accent/10 to-transparent rounded-full transform -translate-x-6 translate-y-6"></div>
      </div>

      {/* Floating elements */}
      <div className="absolute -top-2 -right-2 w-4 h-4 bg-orange-accent rounded-full animate-pulse-gentle"></div>
      <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-blue-primary rounded-full animate-pulse-gentle delay-1000"></div>

      <ConfettiEffect
        trigger={showConfetti}
        type="complete"
        onComplete={() => setShowConfetti(false)}
      />
    </div>
  );
};

export default InteractivePACTsCarousel;
